import { type FieldMetadata, getInputProps } from '@conform-to/react';
import clsx from 'clsx';
import { type ElementType, type ReactNode } from 'react';
import { ErrorMessage, Field, Label } from '../catalyst-ui/fieldset';
import { Input } from '../catalyst-ui/input';
import { Explanation } from '../Explanation';

type Props = {
    inputClassName?: string;
    field: FieldMetadata<any, Record<string, any>>;
    fieldClassName?: string;
    label?: ReactNode | string;
    labelClassName?: string;
    shortExplanation?: ReactNode | string;
    shortExplanationClassName?: string;
    longExplanation?: ReactNode | string;
    control?: ElementType;
} & Omit<Parameters<typeof Input>[0], 'name' | 'invalid' | 'className'>;

export function FormInputField({
    field,
    fieldClassName,
    inputClassName,
    label,
    labelClassName,
    shortExplanation,
    shortExplanationClassName,
    longExplanation,
    type,
    control: Control = Input,
    ...inputProps
}: Props) {
    const { key, ...fieldInputProps } = getInputProps(field, { type: type ?? 'text' as any });
    return (
        <Field className={fieldClassName}>
            {label ? <Label className={clsx('block text-sm font-medium mb-1', labelClassName)}>{label}</Label> : null}
            <Control
                key={key}
                {...{ ...fieldInputProps, ...inputProps }}
                invalid={!field.valid}
                className={inputClassName}
            />
            {shortExplanation || longExplanation
                ? (
                    <Explanation
                        short={shortExplanation}
                        long={longExplanation}
                        shortClassName={shortExplanationClassName}
                    />
                )
                : null}
            {field.valid ? null : field.errors?.map(x => <ErrorMessage key={x}>{x}</ErrorMessage>)}
        </Field>
    );
}
